import { searchAirportCodes } from './airport-codes';

interface GeolocationResult {
  coords: {
    latitude: number;
    longitude: number;
  };
  city?: string;
  country?: string;
  airportCode?: string;
  airportName?: string;
  error?: string;
}

// Cache geolocation result for the session
let cachedLocation: GeolocationResult | null = null;

/**
 * Detect if running on macOS (reusable helper)
 */
function isMacOS(): boolean {
  return navigator.platform.toUpperCase().indexOf('MAC') >= 0 || 
         /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
}

/**
 * Check location permission status (internal helper)
 */
async function checkLocationPermissionInternal(): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> {
  if (!navigator.permissions) {
    console.log('⚠️ Permissions API not available, cannot check permission status');
    return 'unknown';
  }

  try {
    const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
    console.log('📍 Location permission status:', result.state);
    return result.state as 'granted' | 'denied' | 'prompt';
  } catch (error) {
    console.warn('⚠️ Error checking location permission:', error);
    return 'unknown';
  }
}

/**
 * Get user's current location using browser geolocation API
 */
async function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise(async (resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocalización no disponible en este navegador'));
      return;
    }

    // Step 1: Check permission status first
    const permissionStatus = await checkLocationPermissionInternal();
    console.log('📍 Permission check result:', permissionStatus);
    
    const macDetected = isMacOS();
    
    if (permissionStatus === 'denied') {
      const macError = macDetected 
        ? 'Permisos de ubicación denegados. En macOS: Preferencias del Sistema > Privacidad y Seguridad > Servicios de Ubicación (debe estar activado y tu navegador debe tener permisos).'
        : 'Permisos de ubicación denegados. Por favor, habilita la ubicación en la configuración de tu navegador.';
      reject(new Error(macError));
      return;
    }
    
    if (permissionStatus === 'unknown') {
      console.warn('⚠️ No se pudo verificar el estado de permisos - intentando de todas formas...');
    }

    console.log('📍 Requesting geolocation with high accuracy...', macDetected ? '(macOS detectado)' : '');
    
    // Strategy: Try multiple approaches
    // 1. High accuracy first (GPS)
    // 2. Low accuracy (WiFi/Network)
    // 3. Use cached position if available
    
    let attempts = 0;
    const maxAttempts = 3;
    
    const tryGeolocation = (highAccuracy: boolean, useCache: boolean, timeoutMs: number) => {
      attempts++;
      console.log(`📍 Attempt ${attempts}/${maxAttempts}: highAccuracy=${highAccuracy}, useCache=${useCache}, timeout=${timeoutMs}ms`);
      
      const options: PositionOptions = {
        enableHighAccuracy: highAccuracy,
        timeout: timeoutMs,
        maximumAge: useCache ? 300000 : 0 // 5 min cache or force fresh location
      };
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log(`✅ Geolocation SUCCESS! Accuracy: ${position.coords.accuracy}m, lat: ${position.coords.latitude}, lng: ${position.coords.longitude}`);
          resolve(position);
        },
        (error) => {
          const errorDetails = {
            code: error.code,
            message: error.message,
            PERMISSION_DENIED: error.PERMISSION_DENIED,
            POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
            TIMEOUT: error.TIMEOUT,
            attempt: attempts,
            highAccuracy,
            useCache
          };
          
          console.log(`❌ Geolocation attempt ${attempts} failed:`, errorDetails);
          
          // Permission denied - don't retry
          if (error.code === error.PERMISSION_DENIED) {
            reject(new Error('Permisos de ubicación denegados. Por favor, habilita la ubicación en la configuración de tu navegador o sistema.'));
            return;
          }
          
          // Try different strategies - be more aggressive about getting real GPS
          if (attempts < maxAttempts) {
            if (error.code === error.POSITION_UNAVAILABLE && highAccuracy) {
              // GPS failed - try network-based location (WiFi/cell towers) with longer timeout
              console.log('🔄 GPS unavailable, trying network-based location (WiFi/cell) with longer timeout...');
              setTimeout(() => tryGeolocation(false, false, 30000), 1500); // 30s timeout for network
            } else if (error.code === error.POSITION_UNAVAILABLE && !highAccuracy) {
              // Network also failed - wait longer and try once more with even longer timeout
              console.log('🔄 Network location also failed, waiting longer and retrying with extended timeout...');
              setTimeout(() => tryGeolocation(false, true, 40000), 3000); // 40s timeout, allow cache
            } else if (error.code === error.TIMEOUT) {
              // Timeout - try with much longer timeout
              console.log('🔄 Timeout occurred, retrying with much longer timeout...');
              setTimeout(() => tryGeolocation(false, true, 45000), 2000); // 45s timeout
            } else {
              // Other error - try one more time with different settings
              console.log('🔄 Retrying with different settings...');
              setTimeout(() => tryGeolocation(!highAccuracy, true, 35000), 2000);
            }
          } else {
            // All attempts failed - provide helpful error message
            let errorMsg = 'Error al obtener la ubicación';
            
            const macDetected = isMacOS();
            
            if (error.code === error.POSITION_UNAVAILABLE) {
              if (macDetected) {
                // Check if permission was granted but still failing (common macOS issue)
                const permissionWasGranted = permissionStatus === 'granted';
                
                if (permissionWasGranted) {
                  // Use a shorter error message that will be detected by the fallback logic
                  errorMsg = 'POSITION_UNAVAILABLE: Permisos concedidos pero ubicación no disponible (kCLErrorLocationUnknown en macOS). El sistema usará timezone como fallback.';
                } else {
                  errorMsg = 'La información de ubicación no está disponible (kCLErrorLocationUnknown).\n\n' +
                            'Para solucionarlo en macOS:\n' +
                            '1. Ve a Preferencias del Sistema > Privacidad y Seguridad > Servicios de Ubicación\n' +
                            '2. Asegúrate de que "Servicios de Ubicación" esté ACTIVADO\n' +
                            '3. Verifica que tu navegador (Chrome/Safari) tenga permisos de ubicación\n' +
                            '4. Si está activado, prueba reiniciar el navegador';
                }
              } else {
                errorMsg = 'POSITION_UNAVAILABLE: La información de ubicación no está disponible. Verifica que los Servicios de Ubicación estén habilitados en tu sistema.';
              }
            } else if (error.code === error.TIMEOUT) {
              errorMsg = 'TIMEOUT: Tiempo de espera agotado. Verifica tu conexión a internet y que los Servicios de Ubicación estén habilitados.';
            }
            
            console.error('❌ All geolocation attempts failed. Final error:', {
              ...errorDetails,
              isMacOS: macDetected,
              permissionStatus,
              userAgent: navigator.userAgent,
              platform: navigator.platform,
              hasWiFi: 'connection' in navigator ? (navigator.connection as any)?.type : 'unknown'
            });
            
            // Show alert on macOS if permission was granted but still failing
            if (macDetected && permissionStatus === 'granted' && error.code === error.POSITION_UNAVAILABLE) {
              console.warn('⚠️ macOS Issue Detected: Permissions granted but CoreLocation still failing.');
              console.warn('⚠️ This usually means Location Services are disabled at system level.');
              console.warn('⚠️ Check: System Preferences > Privacy & Security > Location Services');
            }
            
            reject(new Error(errorMsg));
          }
        },
        options
      );
    };
    
    // Start with high accuracy, no cache, 20s timeout
    tryGeolocation(true, false, 20000);
  });
}

/**
 * Reverse geocode coordinates to get city and country
 * Using Nominatim OpenStreetMap API (free, no API key required)
 */
async function reverseGeocode(latitude: number, longitude: number): Promise<{ city: string; country: string }> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=en`,
      {
        headers: {
          'User-Agent': 'VoyApp/1.0' // Required by Nominatim
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to reverse geocode location');
    }

    const data = await response.json();
    
    // Extract city name from various possible fields
    const city = data.address?.city || 
                 data.address?.town || 
                 data.address?.municipality ||
                 data.address?.village ||
                 data.address?.county ||
                 data.address?.state_district ||
                 'Unknown City';
                 
    const country = data.address?.country || 'Unknown Country';

    return { city, country };
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    throw new Error('Failed to determine city from coordinates');
  }
}

/**
 * Find the nearest major airport to a given location
 * Optimized to minimize API calls by trying most specific first
 */
async function findNearestAirport(city: string, country: string): Promise<{ code: string; name: string } | null> {
  try {
    // Try most specific first: "City, Country" (most likely to return results)
    const searchQueries = [
      `${city}, ${country}`,  // Most specific
      city,                    // City only
      country                  // Country as fallback
    ];

    for (const query of searchQueries) {
      const airports = await searchAirportCodes(query);
      if (airports && airports.length > 0) {
        const airport = airports[0];
        return {
          code: airport.code,
          name: `${airport.city}, ${airport.country} (${airport.code})`
        };
      }
    }

    // No airport found
    return null;
  } catch (error) {
    console.error('Error finding nearest airport:', error);
    return null;
  }
}

/**
 * Get default airport based on browser language/timezone
 * Currently unused but kept for future reference
 */
// @ts-ignore - Function kept for future use
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function getDefaultAirportByLanguage(): Promise<{ code: string; name: string } | null> {
  try {
    // Try to get country from timezone or language
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const lang = navigator.language || navigator.languages?.[0] || 'en-US';
    
    console.log('🔍 Detecting default airport from timezone:', timezone, 'language:', lang);
    
    // Extract country from language (e.g., 'es-AR' -> 'AR')
    const countryMatch = lang.match(/-([A-Z]{2})$/);
    const countryCode = countryMatch ? countryMatch[1] : null;
    
    // Common airport mappings by country code
    const defaultAirports: Record<string, string> = {
      'AR': 'EZE', // Argentina -> Buenos Aires
      'US': 'JFK', // USA -> New York
      'MX': 'MEX', // Mexico -> Mexico City
      'BR': 'GRU', // Brazil -> São Paulo
      'ES': 'MAD', // Spain -> Madrid
      'FR': 'CDG', // France -> Paris
      'DE': 'FRA', // Germany -> Frankfurt
      'IT': 'FCO', // Italy -> Rome
      'GB': 'LHR', // UK -> London
      'CA': 'YYZ', // Canada -> Toronto
    };
    
    // Try timezone-based detection (e.g., 'America/Argentina/Buenos_Aires' -> 'AR')
    let detectedCountry = countryCode;
    console.log('🔍 Checking timezone:', timezone, 'for country detection');
    
    if (!detectedCountry && timezone) {
      // Argentina timezones
      if (timezone.includes('Argentina') || timezone.includes('Buenos_Aires')) {
        detectedCountry = 'AR';
        console.log('✅ Detected Argentina from timezone:', timezone);
      }
      // USA timezones
      else if (timezone.includes('New_York') || timezone.includes('Chicago') || 
               timezone.includes('Los_Angeles') || timezone.includes('Denver') ||
               timezone.includes('America/New_York') || timezone.includes('America/Chicago') ||
               timezone.includes('America/Los_Angeles') || timezone.startsWith('America/')) {
        detectedCountry = 'US';
        console.log('✅ Detected USA from timezone:', timezone);
      }
      // Mexico timezones
      else if (timezone.includes('Mexico')) {
        detectedCountry = 'MX';
        console.log('✅ Detected Mexico from timezone:', timezone);
      }
      // Brazil timezones
      else if (timezone.includes('Sao_Paulo') || timezone.includes('Brasilia')) {
        detectedCountry = 'BR';
        console.log('✅ Detected Brazil from timezone:', timezone);
      }
      // If we're in America/ timezone but not specific, try US
      else if (timezone.startsWith('America/')) {
        detectedCountry = 'US';
        console.log('✅ Defaulting to USA for America/ timezone:', timezone);
      }
    }
    
    // If still no country detected, try language code
    if (!detectedCountry && countryCode) {
      detectedCountry = countryCode;
      console.log('✅ Using country from language code:', countryCode);
    }
    
    console.log('🔍 Detected country code:', detectedCountry);
    
    if (detectedCountry && defaultAirports[detectedCountry]) {
      const code = defaultAirports[detectedCountry];
      console.log('✅ Using default airport:', code, 'for country:', detectedCountry);
      
      // Only fetch airport name if we don't already have it from the mapping
      // This avoids unnecessary API call when we already have the code
      try {
        const airports = await searchAirportCodes(code);
        if (airports && airports.length > 0 && airports[0].code) {
          const airport = airports[0];
          console.log('📍 Airport name fetched from database');
          return {
            code: airport.code,
            name: `${airport.city}, ${airport.country} (${airport.code})`
          };
        }
      } catch (e) {
        console.log('⚠️ Could not fetch airport name from database, using code only:', e);
      }
      
      // Return with just the code if name lookup fails - this is still valid!
      return { code, name: code };
    }
    
    console.log('❌ No default airport found for country:', detectedCountry);
    return null;
  } catch (error) {
    console.error('❌ Error getting default airport:', error);
    return null;
  }
}

/**
 * Main function to get user's location and nearest airport
 * @param forceRefresh - Force a new location request even if cached
 */
export async function getUserLocationWithAirport(forceRefresh: boolean = false): Promise<GeolocationResult> {
  // Return cached result if available and not forcing refresh
  if (cachedLocation && !forceRefresh && !cachedLocation.error) {
    return cachedLocation;
  }
  
  // Clear cache if force refresh or if previous result had error
  if (forceRefresh || (cachedLocation && cachedLocation.error)) {
    cachedLocation = null;
  }

  try {
    // Get current position
    const position = await getCurrentPosition();
    const { latitude, longitude } = position.coords;

    // Reverse geocode to get city
    const { city, country } = await reverseGeocode(latitude, longitude);

    // Find nearest airport
    const airport = await findNearestAirport(city, country);

    if (!airport) {
      const errorResult: GeolocationResult = {
        coords: { latitude, longitude },
        city,
        country,
        error: 'No se encontró un aeropuerto cercano'
      };
      cachedLocation = errorResult;
      return errorResult;
    }

    const result: GeolocationResult = {
      coords: { latitude, longitude },
      city,
      country,
      airportCode: airport.code,
      airportName: airport.name
    };

    // Cache the result
    cachedLocation = result;
    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Error al obtener ubicación';
    
    console.error('❌ Geolocation failed:', errorMessage);
    
    // Return error without timezone fallback - user should enter origin manually
    const errorResult: GeolocationResult = {
      coords: { latitude: 0, longitude: 0 },
      error: errorMessage
    };
    
    // Don't cache errors - let user try again or enter origin manually
    return errorResult;
  }
}

/**
 * Clear cached location (useful when user changes location)
 */
export function clearLocationCache() {
  cachedLocation = null;
}

/**
 * Check if location permission is granted (exported version)
 * Reuses internal helper to avoid code duplication
 */
export async function checkLocationPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> {
  return checkLocationPermissionInternal();
}